import { Component } from "@angular/core";
import { ComponentBase } from "../../../models/ComponentBase";
import {
    DateTimeTba,
    GetProjectDetailsResponse,
    ProjectTerminalStatement,
    TerminalStatement,
    UpsertProjectRequest,
} from "../../../WebApiClient";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ProjectService } from "../../../services/project.service";
import { map, switchMap } from "rxjs/operators";
import { ToastService } from "../../../services/toast.service";
import { TerminalStatementService } from "../../../services/terminalstatement.service";
import { Guid } from "../../../models/Guid";
import { BlockNavigationIfChange } from "../../../guards/block-onchanged.guard";
import { Observable } from "rxjs";

@Component({
    selector: "app-edit-terminal-statement",
    templateUrl: "./edit-terminalstatement.component.html",
    styleUrls: [
        "./edit-terminalstatement.component.scss",
        "../../../style/sidebarLayout.scss",
        "../../../style/project-details.scss",
    ],
    standalone: false,
})
export class EditTerminalStatementComponent
    extends ComponentBase
    implements BlockNavigationIfChange
{
    project: UpsertProjectRequest = new UpsertProjectRequest();

    statementFormList: FormGroup = new FormGroup({
        statmentFormArray: new FormArray([]),
    });

    terminalStatements: TerminalStatement[] | undefined = undefined;

    hasChanges: () => Observable<boolean> | Promise<boolean> | boolean;

    saving: boolean = false;

    statementForm: FormGroup = new FormGroup({
        informantOrderDate: new FormControl<DateTimeTba | undefined>(undefined),
        informantOrderCase: new FormControl<string>(""),
        informantOrderJob: new FormControl<string>(""),
        informantOrderMachine: new FormControl<string>(""),
        informantOrderMachineJobNumber: new FormControl<string>(""),
        informantOrderReadyDate: new FormControl<DateTimeTba | undefined>(undefined),
        informantOrderNote: new FormControl<string>(""),
        terminalStatementMultipleMachines: new FormControl<string>(""),
        terminalStatementNote: new FormControl<string>(""),
        terminalStatementJobNumber: new FormControl<string>(""),
    });

    constructor(
        route: ActivatedRoute,
        public projectService: ProjectService,
        private toastService: ToastService,
        public termainalStatementService: TerminalStatementService
    ) {
        super();
        let detailsSub = route.params
            .pipe(
                map((params) => params["id"]),
                switchMap((id) => projectService.getProjectDetails(id))
            )
            .subscribe((pd) => this.updateDetails(pd));

        this.subscriptions.push(detailsSub);

        let terminalStatementSub = termainalStatementService
            .getTerminalStatements()
            .subscribe((c) => {
                this.terminalStatements = c;
            });

        this.subscriptions.push(terminalStatementSub);

        this.hasChanges = () => false;
    }

    updateDetails(pd: GetProjectDetailsResponse | undefined): void {
        if (!pd) {
            return;
        }

        let result = new UpsertProjectRequest({
            ...pd,
            projectId: pd.id,
            customerIds: pd.projectCustomers
                .map((pc) => pc.customerId!)
                .filter((cid) => !!cid),
        });

        this.project = result;
        this.statementForm.patchValue(pd);

        this.statementFormArray.clear();

        if (this.project.projectTerminalStatements) {
            this.project.projectTerminalStatements.forEach((pst) => {
                this.statementFormArray.push(
                    new FormGroup({
                        id: new FormControl(pst.id, Validators.required),
                        terminalStatementId: new FormControl(
                            pst.terminalStatementId,
                            Validators.required
                        ),
                        date: new FormControl(pst.date, Validators.required),
                        value: new FormControl(pst.value?.toString().replace(".", ","), [Validators.required, Validators.pattern("^-?\\d+(,\\d+)?$")]),
                    })
                );
            });
        }

        this.statementForm.valueChanges.subscribe(() => {
            this.hasChanges = () => {
                return this.statementForm.dirty && this.statementForm.touched;
            };
        });
    }

    get statementFormArray(): FormArray {
        return this.statementFormList.get("statmentFormArray") as FormArray;
    }

    async save(): Promise<void> {
        await this.update().then(
            (r) => {
                if (r) {
                    this.toastService.add(
                        "Project saved",
                        "Project has been saved",
                        "success"
                    );
                } else {
                    this.toastService.add(
                        "Project not saved",
                        "Project could not be saved",
                        "danger"
                    );
                }
            },
            (error) => {
                this.toastService.reportFailure(
                    "Could not update project",
                    error
                );
            }
        );
    }

    async send(): Promise<void> {
        await this.update(true).then(
            (r) => {
                if (r) {
                    this.toastService.add(
                        "Project saved",
                        "Project has been saved and internal note has been sent",
                        "success"
                    );
                } else {
                    this.toastService.add(
                        "Project not saved",
                        "Project could not be saved",
                        "danger"
                    );
                }
            },
            (error) => {
                this.toastService.reportFailure(
                    "Could not update project",
                    error
                );
            }
        );
    }

    private async update(
        sendHasQuayManOrTerminalCalculationChanged: boolean = false
    ): Promise<boolean> {
        if (this.statementForm.invalid || this.statementFormArray.invalid) {
            return false;
        }

        if (!this.projectService.isValid(this.project)) {
            return false;
        }

        try {
            this.project.informantOrderCase =
                this.statementForm.value.informantOrderCase;
            this.project.informantOrderDate =
                this.statementForm.value.informantOrderDate;
            this.project.informantOrderJob =
                this.statementForm.value.informantOrderJob;
            this.project.informantOrderMachine =
                this.statementForm.value.informantOrderMachine;
            this.project.informantOrderNote =
                this.statementForm.value.informantOrderNote;
            this.project.informantOrderReadyDate =
                this.statementForm.value.informantOrderReadyDate;
            this.project.terminalStatementNote =
                this.statementForm.value.terminalStatementNote;
            this.project.terminalStatementMultipleMachines =
                this.statementForm.value.terminalStatementMultipleMachines;
            this.project.informantOrderMachineJobNumber =
                this.statementForm.value.informantOrderMachineJobNumber;
            this.project.terminalStatementJobNumber =
                this.statementForm.value.terminalStatementJobNumber;

            this.project.sendHasQuayManOrTerminalCalculationChanged =
                sendHasQuayManOrTerminalCalculationChanged;

            this.project.projectTerminalStatements =
                this.statementFormArray.value.map((fg: any) => {
                    return new ProjectTerminalStatement({
                        id: fg.id,
                        terminalStatementId: fg.terminalStatementId,
                        date: fg.date,
                        value: fg.value.toString().replace(",", "."),
                    });
                });

            await this.projectService.upsertProject(this.project);

            this.statementForm.markAsUntouched();

            return true;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    addStatement(): void {
        this.statementFormArray.push(
            new FormGroup({
                id: new FormControl<string>(
                    Guid.newGuid(),
                    Validators.required
                ),
                date: new FormControl<DateTimeTba | undefined>(
                    undefined,
                    Validators.required
                ),
                terminalStatementId: new FormControl("", Validators.required),
                value: new FormControl(0, Validators.required),
            })
        );
    }

    removeStatement(index: number): void {
        this.statementFormArray.removeAt(index);
    }

    revalidate(index: number): void {
        let readValue =
            this.statementFormArray.controls[index].get("date")?.value;
        this.statementFormArray.controls[index]
            .get("date")
            ?.setValue(readValue);
    }

    reInformantorderdate(): void {
        let readValue =
            this.statementForm.controls["informantOrderDate"]?.value;

        this.statementForm.controls["informantOrderDate"]?.setValue(readValue);
    }

    reInformantorderReadydate(): void {
        let readValue =
            this.statementForm.controls["informantOrderReadyDate"]?.value;

        this.statementForm.controls["informantOrderReadyDate"]?.setValue(readValue);
    }
}
